package com.sddamico.swigmore.pages.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.api.PollApi
import com.sddamico.swigmore.components.layouts.Column
import com.sddamico.swigmore.components.layouts.Columns
import com.sddamico.swigmore.components.layouts.ContentColumn
import com.sddamico.swigmore.components.layouts.SignedInPage
import com.sddamico.swigmore.ds.Reveal
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.SwEffects
import com.sddamico.swigmore.ds.SwType
import com.sddamico.swigmore.ds.core.Card
import com.sddamico.swigmore.ds.core.CardVariant
import com.sddamico.swigmore.ds.core.PageTitle
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.feedback.PageTitleSkeleton
import com.sddamico.swigmore.ds.feedback.PushPreview
import com.sddamico.swigmore.ds.feedback.Skeleton
import com.sddamico.swigmore.ds.feedback.SkeletonBlock
import com.sddamico.swigmore.ds.forms.Button
import com.sddamico.swigmore.ds.forms.ButtonStyle
import com.sddamico.swigmore.ds.forms.ButtonVariant
import com.sddamico.swigmore.ds.forms.TextField
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeMono
import com.sddamico.swigmore.models.CopyEditRequest
import com.sddamico.swigmore.models.NotifyPreviewResponse
import com.sddamico.swigmore.models.WeekRequest
import com.sddamico.swigmore.util.AppTab
import com.sddamico.swigmore.util.LoadState
import com.sddamico.swigmore.util.clockTime
import com.sddamico.swigmore.util.easternShortDayTime
import com.sddamico.swigmore.util.longDate
import com.sddamico.swigmore.util.rememberAction
import com.sddamico.swigmore.util.rememberApiResource
import com.sddamico.swigmore.util.weekParam
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Iframe
import org.jetbrains.compose.web.dom.TextArea
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLIFrameElement

/** The commissioner's "Email + Push" screen: exactly what the league gets when the poll publishes. */
@Page
@Composable
fun AnnouncePage() {
  val params = rememberPageContext().route.params
  val requested = weekParam(params["season"], params["week"])
  SignedInPage("Email + Push", activeTab = AppTab.Admin, adminOnly = true) {
    ContentColumn {
      // A bad link shows its error rather than the open week, which copy edits would then land on.
      requested.onFailure { PageTitle("Announcement preview", "Email + Push", lede = it.message) }
      requested.onSuccess { week ->
        when (val preview = rememberApiResource(week) { PollApi.announcement(week) }.state) {
          LoadState.Loading -> AnnounceSkeleton()
          is LoadState.Failed -> Reveal { PageTitle("Announcement preview", "Email + Push", lede = preview.message) }
          is LoadState.Ready -> Reveal { AnnouncePreview(preview.value) }
        }
      }
    }
  }
}

@Composable
private fun AnnouncePreview(initial: NotifyPreviewResponse) {
  var p by remember(initial) { mutableStateOf(initial) }
  val announceAt = p.announceAt
  PageTitle(
    eyebrow = when {
      p.announced -> "Announcement · sent"
      p.published -> "Announcement · not sent, send it from the Commish desk"
      else -> "Announcement preview · poll publishes ${easternShortDayTime(announceAt).uppercase()} ET"
    },
    title = "Email + Push",
    modifier = Modifier.css("margin-bottom" to "24px"),
  )
  Columns {
    Column("520px", Modifier.css("max-width" to "600px")) {
      CopyEditor(p, onSaved = { p = it })
      Card(variant = CardVariant.Paper, padding = "0", modifier = Modifier.css("color" to SwColor.ink, "margin-bottom" to "12px")) {
        Div(
          attrs = Modifier.typeMono("11px", lineHeight = "1.6")
            .css("padding" to "12px 18px", "color" to SwColor.inkSecondary, "white-space" to "pre")
            .toAttrs()
        ) {
          Div { Text("FROM  ${p.from}") }
          Div { Text("TO    all-owners (${p.recipients})") }
          Div(attrs = Modifier.css("color" to SwColor.ink, "white-space" to "normal").toAttrs()) { Text("SUBJ  ${p.subject}") }
        }
      }
      Iframe(attrs = {
        attr("srcdoc", p.emailHtml)
        // No scripts in the commissioner's origin; same-origin only so the height can be read.
        attr("sandbox", "allow-same-origin")
        attr("title", "Announcement email")
        style {
          property("width", "100%"); property("height", "900px"); property("border", "0"); property("display", "block")
          property("border-radius", SwEffects.radiusLg)
        }
        addEventListener("load") { event ->
          val frame = event.target as HTMLIFrameElement
          frame.contentDocument?.documentElement?.scrollHeight?.let { frame.style.height = "${it}px" }
        }
      })
    }
    Column("320px", Modifier.css("max-width" to "360px", "display" to "flex", "flex-direction" to "column", "gap" to "12px")) {
      Div(
        attrs = Modifier.css("background" to SwColor.field900, "border-radius" to "28px", "padding" to "40px 14px 28px",
          "display" to "flex", "flex-direction" to "column", "gap" to "10px").toAttrs()
      ) {
        Div(attrs = Modifier.typeBody("54px", 500, "1").css("text-align" to "center", "color" to SwColor.cream).toAttrs()) {
          Text(clockTime(announceAt).removeSuffix(" AM").removeSuffix(" PM"))
        }
        Div(attrs = Modifier.typeBody("13px", 500, "normal").css("text-align" to "center", "color" to SwColor.textSecondary, "margin-bottom" to "14px").toAttrs()) {
          Text(longDate(announceAt))
        }
        PushPreview(p.pushTitle, p.pushBody, at = "now")
        p.reminders.forEach { PushPreview(it.title, it.body, at = easternShortDayTime(it.at)) }
      }
      Div(attrs = Modifier.typeMono("11px", lineHeight = "1.5").css("color" to SwColor.textMuted).toAttrs()) {
        Text("Push via installable web app (PWA). Reminder pushes fire ${p.reminders.joinToString(", ") { easternShortDayTime(it.at) }} ET to owners who haven't voted; owners without push get them by email.")
      }
      Div(attrs = Modifier.typeMono("11px", lineHeight = "1.5").css("color" to SwColor.textMuted).toAttrs()) {
        Text("PUSH ${if (p.pushConfigured) "ON" else "OFF"}")
      }
      if (p.pushConfigured) TestPushButton()
    }
  }
}

/** The drafted headline and intro, which the commissioner can rewrite until the announcement sends. */
@Composable
private fun CopyEditor(p: NotifyPreviewResponse, onSaved: (NotifyPreviewResponse) -> Unit) {
  val request = rememberAction()
  val rewrite = rememberAction()
  var headline by remember(p) { mutableStateOf(p.headline) }
  var intro by remember(p) { mutableStateOf(p.intro) }
  fun save(edit: CopyEditRequest) = request.run { onSaved(PollApi.saveAnnouncementCopy(edit)) }
  val edited = p.headline != p.draftedHeadline || p.intro != p.draftedIntro
  val unsaved = headline != p.headline || intro != p.intro
  Card(variant = CardVariant.Paper, padding = "18px", modifier = Modifier.css("margin-bottom" to "16px", "color" to SwColor.ink)) {
    Div(attrs = Modifier.typeMono("11px").css("color" to SwColor.inkSecondary, "margin-bottom" to "10px").toAttrs()) {
      Text(when {
        p.announced -> "HEADLINE · SENT"
        edited -> "HEADLINE · EDITED BY THE COMMISSIONER"
        p.draftedByClaude -> "HEADLINE · WRITTEN BY CLAUDE"
        else -> "HEADLINE · DRAFTED FROM THE RESULTS"
      })
    }
    if (p.announced) {
      Div(attrs = Modifier.typeBody("15px", 600, "1.4").toAttrs()) { Text(p.headline) }
      return@Card
    }
    TextField(headline, { headline = it }, label = "Headline")
    TextArea(intro, attrs = Modifier
      .css("width" to "100%", "box-sizing" to "border-box", "min-height" to "220px", "margin-top" to "10px",
        "padding" to "14px", "border-radius" to SwEffects.radiusMd, "border" to "1.5px solid ${SwColor.borderOnCream}",
        "background" to SwColor.creamLight, "color" to SwColor.field700, "font" to "500 15px/1.45 ${SwType.body}",
        "outline" to "none", "resize" to "vertical")
      .toAttrs {
        attr("aria-label", "Intro")
        onInput { intro = it.value }
      })
    Div(attrs = Modifier.css("display" to "flex", "gap" to "8px", "margin-top" to "12px", "flex-wrap" to "wrap").toAttrs()) {
      Button("Save", variant = ButtonVariant.Field, style = ButtonStyle.CapsSm,
        disabled = request.busy || rewrite.busy || !unsaved,
        onClick = {
          // Text matching the draft is stored as no edit, so a later result change still redrafts it.
          save(CopyEditRequest(p.season, p.week,
            headline.takeIf { it != p.draftedHeadline }, intro.takeIf { it != p.draftedIntro }))
        })
      if (edited) Button("Use the draft", variant = ButtonVariant.Ghost, style = ButtonStyle.CapsSm, disabled = request.busy || rewrite.busy,
        onClick = { save(CopyEditRequest(p.season, p.week)) })
      // A saved edit would hide the new draft, and the response resets the fields, so only offer it on the draft as shown.
      if (p.claudeEnabled && !edited) Button(if (rewrite.busy) "Writing…" else "Rewrite with Claude", variant = ButtonVariant.Outline,
        ai = true, style = ButtonStyle.CapsSm, disabled = request.busy || rewrite.busy || unsaved,
        onClick = { rewrite.run { onSaved(PollApi.rewriteAnnouncement(WeekRequest(p.season, p.week))) } })
    }
    if (p.claudeEnabled) {
      Div(attrs = Modifier.typeMono("11px", lineHeight = "1.5").css("color" to SwColor.inkSecondary, "margin-top" to "10px").toAttrs()) {
        Text("Claude writes the draft from the final tally when the poll publishes. Your edits stay on top of it.")
      }
    }
    (request.error ?: rewrite.error)?.let { Div(attrs = Modifier.typeMono("11px").css("color" to SwColor.ink, "margin-top" to "8px").toAttrs()) { Text(it) } }
  }
}

@Composable
private fun TestPushButton() {
  val request = rememberAction()
  var result by remember { mutableStateOf<String?>(null) }
  Button("Send me a test push", variant = ButtonVariant.Outline, style = ButtonStyle.CapsSm, onDark = true, onClick = {
    request.run {
      val r = PollApi.testPush()
      result = if (r.attempted == 0) "No devices enrolled for your account." else "Delivered to ${r.delivered} of ${r.attempted} device(s)."
    }
  })
  (request.error ?: result)?.let { Div(attrs = Modifier.typeMono("11px").css("color" to SwColor.textSecondary).toAttrs()) { Text(it) } }
}

@Composable
private fun AnnounceSkeleton() {
  Skeleton {
    PageTitleSkeleton(Modifier.css("margin-bottom" to "24px"), lede = false)
    Columns {
      Column("520px", Modifier.css("max-width" to "600px")) { SkeletonBlock("560px") }
      Column("320px") { SkeletonBlock("240px") }
    }
  }
}
